import { customUseEffect } from "@common";
import {
  getAssignmentType,
  getCrmTeamApi,
  getProjectUser,
} from "@features";

import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

const validationSchema = Yup.object({
  assignmentType: Yup.object()
    .nullable()
    .required("Assignment Type is required"),
  userId: Yup.object()
    .nullable()
    .required("User is required"),
  isPrimary: Yup.boolean(),
  startDate: Yup.mixed().nullable(),
  endDate: Yup.mixed()
    .nullable()
    .test(
      "is-after-start",
      "End Date cannot be earlier than Start Date",
      function (endDate) {
        const { startDate } = this.parent;
        if (!startDate || !endDate) return true;
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (!start.isValid() || !end.isValid()) return true;
        return !end.isBefore(start, "day");
      }
    ),
});

const getSelectedId = (data, fallbackId) => {
  if (Array.isArray(data)) {
    return data[0]?.id || fallbackId || null;
  }
  return data?.id || fallbackId || null;
};

const getAssignmentTypeValue = (assignmentType) => {
  if (typeof assignmentType === "string") {
    return assignmentType;
  }
  return assignmentType?.assignmentType || assignmentType?.name || "";
};

const getUserIdValue = (userId) => {
  return (
    userId?.id ||
    userId?.userId ||
    (typeof userId === "string" ? userId : null)
  );
};

const formatDate = (date) => {
  if (date && dayjs(date).isValid()) {
    return dayjs(date).format("YYYY-MM-DD");
  }
  return null;
};

const isRequestSuccessful = (res) =>
  Boolean(
    res?.payload?.ok ||
    res?.payload?.data?.ok ||
    res?.meta?.requestStatus === "fulfilled"
  );

const resolveFindAssignmentType = (data, assignmentTypeOptions) => {
  if (!data?.assignmentType) return null;
  if (typeof data?.assignmentType === "object") return data.assignmentType;
  return (
    assignmentTypeOptions.find(
      (i) =>
        i?.assignmentType === data.assignmentType ||
        i?.name === data.assignmentType
    ) || { assignmentType: data.assignmentType }
  );
};

const resolveFindUser = (data, projectUsersOptions) => {
  if (!data?.userId && !data?.user) return null;
  if (typeof data?.user === "object" && data?.user) return data.user;
  const uid = data?.userId;
  return (
    projectUsersOptions.find(
      (u) => u?.id === uid || u?.userId === uid || u?.email === uid
    ) || (uid ? { userName: uid, id: uid } : null)
  );
};

export default function useCrmTeamForm({
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forCustomer,
  forContact,
  forContract,
  forOrder,
  campaignId,
  prospectId,
  inquiryId,
  leadId,
  opportunityId,
  customerAccountId,
  customerId,
  contactId,
  contractId,
  orderId,
  data,
  onSuccess,
  ...props
}) {
  const dispatch = useDispatch();

  const campaignSelectedData = useSelector((state) => state?.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state?.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state?.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state?.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state?.account?.selectedData);
  const customerSelectedData = useSelector((state) => state?.customer?.selectedData);
  const contactSelectedData = useSelector((state) => state?.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state?.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state?.order?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forCustomer,
    forContact,
    forContract,
    forOrder,
    campaignId,
    prospectId,
    inquiryId,
    leadId,
    opportunityId,
    customerAccountId,
    customerId,
    contactId,
    contractId,
    orderId,
    ...props,
  };

  const api = useMemo(
    () => getCrmTeamApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forCustomer,
      forContact,
      forContract,
      forOrder,
      campaignId,
      prospectId,
      inquiryId,
      leadId,
      opportunityId,
      customerAccountId,
      customerId,
      contactId,
      contractId,
      orderId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const propId = propsWithFlags?.[api.idKey];
    if (typeof propId === "string" || typeof propId === "number") {
      return propId;
    }
    switch (api.entityType) {
      case "contract":
        return contractId || getSelectedId(contractSelectedData, data?.contractId);
      case "customer-account":
        return customerAccountId || getSelectedId(accountSelectedData, data?.customerAccountId);
      case "contact":
        return contactId || getSelectedId(contactSelectedData, data?.contactId);
      case "opportunity":
        return opportunityId || getSelectedId(opportunitySelectedData, data?.opportunityId);
      case "lead":
        return leadId || getSelectedId(leadSelectedData, data?.leadId);
      case "campaign":
        return campaignId || getSelectedId(campaignSelectedData, data?.campaignId);
      case "inquiry":
        return inquiryId || getSelectedId(inquirySelectedData, data?.inquiryId);
      case "prospect":
        return prospectId || getSelectedId(prospectSelectedData, data?.prospectId);
      case "customer":
        return customerId || getSelectedId(customerSelectedData, data?.customerId);
      case "order":
        return orderId || getSelectedId(orderSelectedData, data?.orderId);
      default:
        return null;
    }
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    contractId,
    customerAccountId,
    contactId,
    opportunityId,
    leadId,
    campaignId,
    inquiryId,
    prospectId,
    customerId,
    orderId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
    data,
  ]);

  const assignmentTypeOptions =
    useSelector((state) => state?.assignmentType?.data) || [];
  const projectUsersOptions =
    useSelector((state) => state?.projectUsers?.data) || [];

  customUseEffect(() => {
    dispatch(getAssignmentType());
    dispatch(getProjectUser());
  }, [dispatch]);

  const findAssignmentType = useMemo(
    () => resolveFindAssignmentType(data, assignmentTypeOptions),
    [data, assignmentTypeOptions]
  );

  const findUser = useMemo(
    () => resolveFindUser(data, projectUsersOptions),
    [data, projectUsersOptions]
  );

  const initialStartDate = useMemo(() => {
    const raw = data?.startDate || data?.start_date;
    return raw && dayjs(raw).isValid() ? dayjs(raw) : null;
  }, [data?.startDate, data?.start_date]);

  const initialEndDate = useMemo(() => {
    const raw = data?.endDate || data?.end_date;
    return raw && dayjs(raw).isValid() ? dayjs(raw) : null;
  }, [data?.endDate, data?.end_date]);

  const formik = useFormik({
    initialValues: {
      assignmentType: findAssignmentType,
      userId: findUser,
      isPrimary: data?.isPrimary ?? false,
      startDate: initialStartDate,
      endDate: initialEndDate,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...(data?.id ? { id: data.id } : {}),
        ...(activeEntityId ? { [api.idKey]: activeEntityId } : {}),
        assignmentType: getAssignmentTypeValue(values?.assignmentType),
        userId: getUserIdValue(values?.userId),
        isPrimary: Boolean(values?.isPrimary),
        startDate: formatDate(values?.startDate),
        endDate: formatDate(values?.endDate),
      };

      const action = data?.id ? api.update(payload) : api.add(payload);
      const res = await dispatch(action);

      if (isRequestSuccessful(res)) {
        onSuccess?.();
        return true;
      }
      return false;
    },
  });

  return {
    formik,
    options: {
      assignmentTypeOptions,
      projectUsersOptions,
    },
  };
}
